import { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
  useTheme,
  styled,
} from '@mui/material';
import { LdtOrderRepository } from '@ldt/api/order_repository';
import { PreventLeaveDuringUploadDialog } from '@ldt/features/orders/components/prevent_leave_during_upload_dialog';
import { UploadedFileItem } from '@ldt/features/orders/components/uploaded_file_item';
import { useLdtFileUploads } from '@ldt/features/orders/hooks/use_ldt_file_uploads';
import { CloudUpload } from 'src/components/icons/cloud_upload';
import { LdtOrderDetailFieldsFragment, UploadedFileCategory } from 'src/types';
import { WorkflowType } from 'src/types/workflows';

type UploadFileCategoryConfig = {
  label: string;
  category: UploadedFileCategory;
};

const CLINIC_UPLOAD_FILE_CATEGORIES: UploadFileCategoryConfig[] = [
  {
    label: 'Demographic / Face Sheet',
    category: UploadedFileCategory.FaceSheet,
  },
  {
    label: 'Pathology Report',
    category: UploadedFileCategory.PathologyReport,
  },
  {
    label: 'Insurance Card(s)',
    category: UploadedFileCategory.Insurance,
  },
];

/** Matches document categories CS/Sales can upload on clinic metastatic orders. */
const TEMPUS_UPLOAD_FILE_CATEGORIES: UploadFileCategoryConfig[] = [
  {
    label: 'Test Requisition Form',
    category: UploadedFileCategory.TestRequisitionForm,
  },
  ...CLINIC_UPLOAD_FILE_CATEGORIES,
  {
    label: 'Other(s)',
    category: UploadedFileCategory.Other,
  },
];

function getUploadFileCategories(
  workflowType: WorkflowType,
): UploadFileCategoryConfig[] {
  if (workflowType === WorkflowType.TEMPUS) {
    return TEMPUS_UPLOAD_FILE_CATEGORIES;
  }

  // Clinic, ancillary lab, and direct upload lab share the same upload categories.
  if (
    workflowType === WorkflowType.CLINIC ||
    workflowType === WorkflowType.ANCILLARY_LAB ||
    workflowType === WorkflowType.DIRECT_UPLOAD_LAB
  ) {
    return CLINIC_UPLOAD_FILE_CATEGORIES;
  }

  throw new Error(`Invalid workflow type: ${workflowType}`);
}

interface UploadFilesDialogProps {
  open: boolean;
  onClose: () => void;
  order: LdtOrderDetailFieldsFragment;
  orderRepository: LdtOrderRepository;
  workflowType: WorkflowType;
}

const CategoryButton = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected?: boolean }>(({ theme, selected }) => ({
  height: '70px',
  padding: theme.spacing(3),
  margin: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  boxShadow: theme.shadows[3],
  color: selected
    ? theme.palette.primary.contrastText
    : theme.palette.text.primary,
  backgroundColor: selected
    ? theme.palette.primary.main
    : theme.palette.background.paper,
  position: 'relative',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: selected
      ? theme.palette.primary.main
      : theme.palette.action.hover,
    color: selected
      ? theme.palette.primary.contrastText
      : theme.palette.text.primary,
  },
  borderRadius: theme.spacing(0.5),
  ...(selected && {
    '&::after': {
      content: '""',
      position: 'absolute',
      right: '-10px',
      top: '50%',
      transform: 'translateY(-50%)',
      width: 0,
      height: 0,
      borderTop: '10px solid transparent',
      borderBottom: '10px solid transparent',
      borderLeft: `10px solid ${theme.palette.primary.main}`,
      zIndex: 1,
    },
  }),
}));

const CategoryButtonUploadCounter = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected?: boolean }>(({ theme, selected }) => ({
  width: '34px',
  height: '34px',
  padding: theme.spacing(1.25),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing(1.25),
  flexShrink: 0,
  borderRadius: theme.spacing(4.5),
  border: '1px solid',
  borderColor: selected
    ? theme.palette.primary.contrastText
    : theme.palette.text.secondary,
}));

export const UploadFilesDialog = ({
  open,
  onClose,
  order,
  orderRepository,
  workflowType,
}: UploadFilesDialogProps) => {
  const theme = useTheme();
  const categories = getUploadFileCategories(workflowType);
  const [selectedCategory, setSelectedCategory] =
    useState<UploadedFileCategory | null>(null);
  // Use the LDT file uploads hook
  const {
    getFilesForCategory,
    handleFileSelected,
    handleFileRemoved,
    isFileDeleting,
  } = useLdtFileUploads({
    order,
    orderRepository,
  });

  // Reset selected category when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedCategory(categories[0]?.category ?? null);
    }
  }, [open, categories]);

  // Get files for the selected category
  const selectedCategoryFiles = selectedCategory
    ? getFilesForCategory(selectedCategory)
    : [];

  // File upload handler for selected category
  const handleFileDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (selectedCategory && handleFileSelected) {
        for (const file of acceptedFiles) {
          handleFileSelected(file, selectedCategory);
        }
      }
    },
    [selectedCategory, handleFileSelected],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
    },
    noClick: false,
    noDrag: false,
    multiple: true,
  });

  const handleClose = () => {
    setSelectedCategory(null);
    onClose();
  };

  const selectedCategoryLabel =
    categories.find((cat) => cat.category === selectedCategory)?.label || '';

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth='md'
        fullWidth
        data-testid='upload-files-dialog'
      >
        <DialogTitle sx={{ textAlign: 'left' }}>Upload Files</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              gap: 2,
              minHeight: '400px',
            }}
          >
            {/* Left side - Category selection */}
            <Box
              data-testid='upload-files-category-list'
              sx={{
                flexGrow: 1,
                minWidth: '200px',
                maxWidth: '350px',
                display: 'flex',
                flexDirection: 'column',
                gap: 2.5,
              }}
            >
              {categories.map((cat) => (
                <CategoryButton
                  key={cat.category}
                  selected={selectedCategory === cat.category}
                  onClick={() => setSelectedCategory(cat.category)}
                  elevation={selectedCategory === cat.category ? 2 : 0}
                  data-testid={`upload-files-category-${cat.category.toLowerCase()}`}
                >
                  <Typography
                    variant='body2'
                    sx={{
                      fontWeight:
                        selectedCategory === cat.category
                          ? theme.typography.fontWeightMedium
                          : theme.typography.fontWeightRegular,
                    }}
                  >
                    {cat.label}
                  </Typography>
                  <CategoryButtonUploadCounter
                    selected={selectedCategory === cat.category}
                  >
                    <Typography variant='body2'>
                      {getFilesForCategory(cat.category).length}
                    </Typography>
                  </CategoryButtonUploadCounter>
                </CategoryButton>
              ))}
            </Box>

            {/* Right side - Uploader for selected category */}
            <Box
              sx={{
                flexGrow: 1,
                minWidth: '400px',
                flex: 1,
                p: 2,
                m: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                boxShadow: 3,
              }}
            >
              {selectedCategory && (
                <>
                  <Typography
                    variant='subtitle1'
                    sx={{
                      fontWeight: 'medium',
                    }}
                  >
                    {selectedCategoryLabel}
                  </Typography>

                  {/* Drag and drop area */}
                  <Box {...getRootProps()} data-testid='upload-files-dropzone'>
                    <Paper
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        border: (theme) =>
                          `1px dashed ${
                            isDragActive
                              ? theme.palette.text.primary
                              : theme.palette.primary.main
                          }`,
                        borderRadius: '6px',
                        cursor: 'pointer',
                        backgroundImage: 'none',
                        '&:hover': {
                          borderColor: (theme) => theme.palette.text.primary,
                          backgroundColor: (theme) =>
                            theme.palette.mode === 'dark'
                              ? theme.palette.primary.dark
                              : theme.palette.primary.light,
                        },
                      }}
                    >
                      <input {...getInputProps()} />
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
                        <CloudUpload
                          sx={{
                            mr: 2,
                            fontSize: (theme) => theme.typography.h3.fontSize,
                            color: (theme) => theme.palette.text.secondary,
                          }}
                        />
                        <Typography
                          variant='body1'
                          sx={{
                            color: 'text.primary',
                          }}
                        >
                          Click or Drag and Drop to upload
                        </Typography>
                        <Typography
                          variant='subtitle2'
                          sx={{
                            color: 'text.secondary',
                          }}
                        >
                          pdf / png / jpg / jpeg types are supported
                        </Typography>
                      </Box>
                    </Paper>
                  </Box>

                  {/* Display uploaded files */}
                  {selectedCategoryFiles.length > 0 && (
                    <Box
                      data-testid='upload-files-list'
                      sx={{
                        maxHeight: '300px',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                      }}
                    >
                      {selectedCategoryFiles.map((file, index) => (
                        <UploadedFileItem
                          key={file.id || index}
                          file={file}
                          onRemove={(fileId) => handleFileRemoved(fileId)}
                          index={index}
                          isRemoving={isFileDeleting?.(file.id)}
                        />
                      ))}
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              width: '100%',
            }}
          >
            <Button
              variant='contained'
              onClick={handleClose}
              size='large'
              data-testid='upload-files-done-button'
            >
              DONE
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <PreventLeaveDuringUploadDialog orderId={order.id} />
    </>
  );
};
